import React, { useState, useMemo } from 'react';
import { Button, Form, Input, notification, Text, Icon } from 'tea-component';
import { useIntl } from 'umi';
import classNames from "classnames";
import { useRequest } from "ahooks";
import { savePersonalData } from "@/pages/datalab/pages/Number/PersonalData/service";
import { ISaveTableInfoProps, ITableInfo, PERSONAL_TABLE_NAME_REGEX } from './types';
import './index.scss';

// 校验单个表名并返回错误信息
const validateTableName = (name: string): string => {
  const trimmedName = name.trim();
  
  if (trimmedName.length < 1) {
    return 'dbl_personal_data_name_empty';
  }
  
  if (trimmedName.length > 200) {
    return 'dbl_personal_data_name_too_long';
  }
  
  if (!PERSONAL_TABLE_NAME_REGEX.test(trimmedName)) {
    return 'dbl_personal_data_name_invalid_format';
  }
  
  return '';
};

// 表名称上面的 标题和保存是否成功的状态展示
const SheetTitleWithStatus = (props: any) => {
  // status 无值时, 代表还没有提交过这个表
  // status 为 success, 说明已经提交过, 且后端创建数据表成功
  // status 为其他值, 说明是后端返回的错误信息, 需要展示在标题右侧
  // sheetName 文件是excel类型时 才展示 sheetName.
  const { sheetName, status='' } = props;
  const { formatMessage } = useIntl();
  return (<div className={classNames("dbl-save-table-info__sheet-title", {"dbl-save-table-info__sheet-no-title": !sheetName})}>
    <div className={'dbl-sheet-title-name'} style={{maxWidth: status ? '50%' : '100%'}}>{sheetName}</div>
    {
      status ? (status === 'success') ? (<div className={'dbl-sheet-status'}><Icon type="success" /><Text className={'dbl-save-table-info__sheet-title-info'}> {formatMessage({id: 'dbl_personal_data_upload_success'})}</Text></div>) : (<div className={'dbl-sheet-status'}><Icon type="error" /><Text theme="danger" className={'dbl-save-table-info__sheet-title-info'}> {formatMessage({id: 'dbl_personal_data_upload_fail'})} {status}</Text></div>) : ''
    }
  </div>);
};

const SaveTableInfo: React.FC<ISaveTableInfoProps> = ({
  defaultFormData,
  uploadTableFile,
  gameCode,
  onPrevStep,
  onSubmitSuccess,
}) => {
  const {formatMessage} = useIntl();
  
  // 初始化表单数据，为每个选中的 sheet 创建对应的表单项
  const [formData, setFormData] = useState<ITableInfo[]>(defaultFormData || []);
  
  // 校验错误状态 - 使用 sheet_name 作为 key，错误信息作为 value
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>(
    defaultFormData?.reduce((acc: Record<string, string>, item: ITableInfo) => {
      const errorMessage = validateTableName(item.name);
      if (errorMessage) {
        acc[item.sheet_name] = errorMessage;
      }
      return acc;
    }, {}) || {}
  );

  const [saveStatus, setSaveStatus] = useState<Record<string, string>>(defaultFormData?.reduce((acc, data) => {
    data.saveStatus && (acc[data.sheet_name] = data.saveStatus);
    return acc;
  }, {} ) || {});
  
  const { loading, run: saveTables } = useRequest(savePersonalData,
    {
      manual: true,
      onSuccess: (data: any) => {
        const { failed, succeed } = data || {};
        if (failed?.length > 0) {
          // 说明有 数据表没有创建成功, 要提示用户
          const tempFileName = uploadTableFile?.file?.name?.replace(/\.[^/.]+$/, '') || 'csv文件';
          const status = {};
          failed?.forEach((item: any) => {
            const key = item.sheet_name || tempFileName;
            // @ts-ignore
            status[key] = item.err_msg;
          });
          // 同时要存储 数据表创建成功的信息, 用于页面显示成功提示
          succeed?.forEach((item: any) => {
            const key = item.sheet_name || tempFileName;
            // @ts-ignore
            status[key] = 'success';
          });
          setSaveStatus(status);
          return;
        }
        notification.success({
          description: formatMessage({id: 'dbl_personal_data_save_success'})
        });
        onSubmitSuccess?.();
      },
      onError: (error) => {
        console.log("保存 Personal Data 错误", error);
        // 重命名错误
        // 其他错误, 左上角弹窗
      }
    });
  
  // 处理单个表单项的输入变化
  const handleInputChange = (index: number, field: keyof ITableInfo, value: string): void => {
    setFormData(prev => prev.map((item, itemIndex) =>
      itemIndex === index ? { ...item, [field]: value } : item
    ));
    
    // 如果是表名字段，进行实时校验
    if (field === 'name') {
      const currentItem = formData[index];
      const validationError = validateTableName(value);
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        if (validationError) {
          newErrors[currentItem.sheet_name] = validationError;
        } else {
          delete newErrors[currentItem.sheet_name];
        }
        return newErrors;
      });
    }
  };

  // 检查是否有任何校验错误
  const hasValidationErrors = useMemo(() => Object.keys(validationErrors).length > 0, [validationErrors]);
  
  // 获取错误提示文本
  const getErrorMessage = (index: number): string => {
    const currentItem = formData[index];
    const errorKey = validationErrors[currentItem.sheet_name];
    
    if (errorKey) {
      return formatMessage({ id: errorKey });
    }
    
    return '';
  };
  
  // 处理提交
  const handleSubmit = (): void => {
    // 需要对提交的信息进行过滤
    // 当 saveStatus 不存在时, 说明没有提交过, 可以进行提交
    // 当 saveStatus === 'success', 说明已经提交成功了, 不需要再提交了.
    saveTables({
      file_path: uploadTableFile.fileUrlInfo?.url || uploadTableFile.fileUrlInfo?.key || '',
      file_type: uploadTableFile.mode || '',
      file_size: uploadTableFile.file?.size || 0,
      split: uploadTableFile.delimiterKey || 'comma',
      game_code: gameCode,
      data: formData?.filter((data: any) => !data.saveStatus || data.saveStatus !== 'success'),
    });
  };

  return (
    <div className="dbl-save-table-info">
      <div className="dbl-save-table-info__content">
        <Form>
          {formData.map((item, index) => (
            <div key={item.sheet_name} className="dbl-save-table-info__sheet-section">
              <SheetTitleWithStatus sheetName={uploadTableFile?.mode === 'excel' ? item.sheet_name : undefined} status={saveStatus?.[item.sheet_name]}/>
              <Form.Item
                label={formatMessage({ id: 'dbl_personal_data_name_label' })}
                required
                showStatusIcon={false}
                status={getErrorMessage(index) ? 'error' : undefined}
                message={getErrorMessage(index)}
              >
                <Input
                  className={classNames('dbl-save-table-info__name-input', {'dbl-save-table-info__name-input--error': getErrorMessage(index)})}
                  value={item.name}
                  onChange={(value) => handleInputChange(index, 'name', value)}
                  placeholder={formatMessage({ id: 'dbl_personal_data_enter_table_name' })}
                  disabled={saveStatus?.[item.sheet_name] === 'success'}
                />
              </Form.Item>
              
              <Form.Item
                label={formatMessage({ id: 'dbl_personal_data_description_label' })}
                className={classNames({'dbl-save-table-info__description-textarea-disabled': saveStatus?.[item.sheet_name] === 'success'})}
              >
                <Input.TextArea
                  className={'dbl-save-table-info__description-textarea'}
                  value={item.description}
                  onChange={(value) => handleInputChange(index, 'description', value)}
                  placeholder={formatMessage({ id: 'dbl_personal_data_enter_table_description' })}
                  maxLength={2560}
                  showCount
                  disabled={saveStatus?.[item.sheet_name] === 'success'}
                />
              </Form.Item>
            </div>
          ))}
        </Form>
      </div>

      <div className="dbl-save-table-info__actions">
        <Button onClick={() => {
          const newDatas = formData.map(data => {
            // 将后端返回的错误信息存储起来
            data.saveStatus = saveStatus?.[data.sheet_name];
            return data;
          });
          onPrevStep?.(newDatas);
        }}>{formatMessage({ id: 'dbl_personal_data_prev_step' })}</Button>
        <Button 
          type="primary" 
          onClick={handleSubmit}
          disabled={hasValidationErrors}
          loading={loading}
        >
          {formatMessage({ id: 'dbl_personal_data_submit' })}
        </Button>
      </div>
    </div>
  );
};

export default SaveTableInfo;
